import EventHandler from '@pixelunion/events';
import Flickity from 'flickity';
import loadYoutubeIframeAPI from '../utils/youtubeIframeAPIHelper';
import FLICKITY_ARROW_ICON from '../assets/flickityArrowIcon';

export default class ProductGallery {
  constructor(el, settings) {
    this.el = el;
    this.settings = settings;
    this.viewport = this.el.querySelector('[data-product-gallery]');
    this.figures = this.viewport.querySelectorAll('[data-product-gallery-figure]');
    this.media = [];
    this.selected = null;

    try {
      this.modelJson = JSON.parse(this.viewport.dataset.productModels);
    } catch (error) {
      console.warn('Unable to parse model data');
    }

    this.events = new EventHandler();

    const features = [];

    if (this.el.querySelector('[data-media-type="model"]')) {
      this.events.register(this.viewport, 'click', e => {
        if ('shopifyXr' in e.target.dataset) {
          this._onViewInYourSpaceClick(e.target);
        }
      });

      features.push(
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: () => this._onModelLibraryLoad(),
        },
      );
      features.push(
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: () => this._onShopifyXRLoad(),
        },
      );
    }

    if (this.el.querySelector('[data-media-type="video"]')) {
      Promise.all([
        new Promise(resolve => {
          if (!document.querySelector('#plyr-stylesheet')) {
            const link = document.createElement('link');

            link.setAttribute('id', 'plyr-stylesheet');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css');
            link.onload = resolve;
            document.body.appendChild(link);
          } else {
            resolve();
          }
        }),
        new Promise(resolve => {
          features.push({
            name: 'video-ui',
            version: '1.0',
            onLoad: resolve,
          });
        }),
      ]).then(() => this._onVideoLibraryLoad());
    }

    loadYoutubeIframeAPI().then(() => this._onExternalVideoLibraryLoad());

    if (features.length) {
      Shopify.loadFeatures(features);
    }
  }

  initFlickity() {
    this.flickity = new Flickity(this.viewport, {
      pageDots: false,
      prevNextButtons: this.figures.length > 1,
      arrowShape: FLICKITY_ARROW_ICON,
      wrapAround: true,
    });

    this.selected = this.figures[0];

    const playIfModel = figure => {
      if (figure.dataset.mediaType === 'model') {
        const model = this.media.filter(media => media.figure === figure)[0];
        if (model) {
          model.play();
        }
      }
    };

    const autoplayVideo = figure => {
      if (figure.dataset.mediaType === 'video' || figure.dataset.mediaType === 'external_video') {
        const video = this.media.filter(media => media.figure === figure)[0];
        if (video) {
          if (this.settings.enableVideoAutoplay) {
            video.play();
          }
          video.focus();
        }
      }
    };

    this.flickity.on('change', index => {
      const figure = this.figures[index];
      this.selected = figure;
      this._pauseMedia();
      autoplayVideo(figure);
      playIfModel(figure);
    });

    // Flickity controls the pointerdown event which prevents model-viewer-ui from
    // triggering .play() on mouseup. This returns this functionality.
    this.flickity.on('staticClick', (event, pointer, cellElement) => playIfModel(cellElement));
  }

  selectMediaByVariant(variant) {
    if (!variant.featured_media) return;

    const figure = this.viewport.querySelector(`[data-media="${variant.featured_media.id}"]`);
    this._selectMediaByIndex(figure.dataset.productGalleryFigure);
  }

  pause() {
    this.media.forEach(m => m.pause());
  }

  unload() {
    this.events.unregisterAll();

    this.media.forEach(m => m.unload());

    if (this.flickity) {
      this.flickity.destroy();
    }
  }

  _onModelLibraryLoad() {
    const onPlayListener = () => {
      if (this.flickity) {
        this.flickity.unbindDrag();
      }
    };
    const onPauseListener = () => {
      if (this.flickity) {
        this.flickity.bindDrag();
      }
    };

    const modelFigures = this.viewport.querySelectorAll('[data-media-type="model"]');
    const controls = ['zoom-in', 'zoom-out'];
    if (document.fullscreenEnabled) controls.push('fullscreen');

    modelFigures.forEach(figure => {
      const modelEl = figure.querySelector('model-viewer');
      const player = new Shopify.ModelViewerUI(modelEl, { controls });
      this.media.push({
        type: 'model',
        figure,
        player,
        focus: () => {},
        restart: () => {},
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      });
      this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_play', onPlayListener);
      this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_pause', onPauseListener);
    });
  }

  _onViewInYourSpaceClick(target) {
    if (!this.selected || target.dataset.shopifyModel3dId === this.selected.dataset.media) return;

    const figure = this.viewport.querySelector(`[data-media="${target.dataset.shopifyModel3dId}"]`);
    this._selectMediaByEl(figure);
  }

  _selectMediaByEl(el) {
    this._selectMediaByIndex(parseInt(el.dataset.productGalleryFigure, 10));
  }

  _onShopifyXRLoad() {
    if (this.modelJson) {
      window.ShopifyXR = window.ShopifyXR || function() {(ShopifyXR.q = ShopifyXR.q||[] ).push(arguments); }; // eslint-disable-line
      if (window.ShopifyXR.addModels) {
        window.ShopifyXR.addModels(this.modelJson);
      } else {
        window.ShopifyXR('addModels', this.modelJson);
      }
    }
  }

  _onVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="video"]');

    videoFigures.forEach(videoFigure => {
      const player = new Shopify.Plyr(
        videoFigure.querySelector('video'),
        {
          loop: { active: this.settings.enableVideoLooping },
        },
      );
      const plyrEl = videoFigure.querySelector('.plyr');
      const video = {
        type: 'video',
        figure: videoFigure,
        player,
        focus: () => plyrEl.focus(),
        restart: () => {
          player.restart();
          player.play();
        },
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      };

      this.events.register(videoFigure, 'play', () => this._pauseMedia(video));
      this.media.push(video);
    });
  }

  _onExternalVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="external_video"]');

    videoFigures.forEach(videoFigure => {
      let playerReady = false;
      const player = new YT.Player(videoFigure.querySelector('iframe'), {
        events: {
          onReady: () => { playerReady = true; },
        },
      });
      const video = {
        type: 'video',
        figure: videoFigure,
        player,
        focus: () => player.getIframe().focus(),
        restart: () => {
          if (!playerReady) return;
          player.seekTo(0);
          player.playVideo();
        },
        pause: () => playerReady && player.pauseVideo(),
        play: () => playerReady && player.playVideo(),
        unload: () => {},
      };

      this.events.register(player, 'onStateChange', ({ data }) => {
        if (data === YT.PlayerState.ENDED && this.settings.enableVideoLooping) {
          video.restart();
        } else if (data === YT.PlayerState.PLAYING) {
          this._pauseMedia(video);
        }
      });
      this.media.push(video);
    });
  }

  _pauseMedia(excludeMedia = null) {
    this.media.forEach(m => {
      if (m !== excludeMedia) {
        m.pause();
      }
    });

    this.settings.pauseOtherGalleries(this);
  }

  _selectMediaByIndex(index) {
    if (!this.flickity) return;

    this.flickity.select(index);
  }
}
